import React, { useState } from 'react';
import {
  BookOpen, Users, ChevronRight, ChevronDown, ChevronUp,
  Heart, Info, Award, CheckCircle, FileText, Target, School, Baby, Lightbulb, Shield
} from 'lucide-react';
import { BackButton, ExpandableCard, FeatureList, FAQAccordion, ResourceLinkGrid } from './shared';
import { DirectoryLink } from './shared';
import { LEVEL_COUNTS } from '../lib/schoolDirectory';

// ==================== PEP INFO SECTION ====================
const PEPInfoSection = ({ setPage }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6 mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-blue-800">Primary Exit Profile (PEP)</h2>
            <p className="text-sm text-blue-600">The Grade 6 Assessment</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
      </button>

      {expanded && (
        <div className="mt-6 space-y-4">
          <p className="text-blue-700">
            PEP replaced GSAT in 2019. It determines which high school your child attends and
            assesses critical thinking more than memorization.
          </p>

          {/* PEP Components */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-2">Performance Task</h3>
              <p className="text-sm text-gray-600">
                Problem-solving in real-world situations. Tests how children apply knowledge.
              </p>
              <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block">
                February (Grade 6)
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-2">Ability Test</h3>
              <p className="text-sm text-gray-600">
                Measures reasoning ability - verbal, mathematical, and spatial reasoning.
              </p>
              <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block">
                March (Grade 6)
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-bold text-gray-800 mb-2">Curriculum Test</h3>
              <p className="text-sm text-gray-600">
                Tests content from the Primary curriculum - Language Arts, Mathematics, Science, Social Studies.
              </p>
              <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded inline-block">
                March (Grade 6)
              </div>
            </div>
          </div>

          {/* Key Information */}
          <div className="bg-white rounded-xl p-4">
            <h4 className="font-semibold text-gray-800 mb-3">What Parents Need to Know</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                Results determine high school placement - children list 5 school preferences
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                Preparation should start early - from Grade 4 onward
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                Results released in May, placement notifications follow
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                Appeals process available for placement disputes
              </li>
            </ul>
          </div>

          <button
            onClick={() => setPage('exams-pep')}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            Full PEP Guide <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

// ==================== CHOOSING A SCHOOL SECTION ====================
const ChoosingSchoolSection = () => {
  const [expanded, setExpanded] = useState(false);

  const factors = [
    { title: 'Location & Transport', desc: 'Consider travel time. Children learn better when not exhausted from long commutes.' },
    { title: 'Class Sizes', desc: 'Smaller classes mean more individual attention. Ask about teacher-to-student ratios.' },
    { title: 'Teaching Approach', desc: 'Some schools are more traditional, others more activity-based. Visit to see the style.' },
    { title: 'Facilities', desc: 'Check for libraries, computer labs, science corners, and safe play areas.' },
    { title: 'Feeding Programme', desc: 'Many public schools offer breakfast and lunch - important for working parents.' },
    { title: 'Extra-Curricular', desc: 'Sports, music, drama - these help children develop beyond academics.' },
    { title: 'Special Needs Support', desc: 'If your child needs extra support, ask what resources are available.' },
    { title: 'School Culture', desc: 'Visit during school hours if possible. The atmosphere matters.' }
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
            <Target className="w-6 h-6 text-brand-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Choosing a Primary School</h2>
            <p className="text-sm text-gray-500">What to look for and questions to ask</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
      </button>

      {expanded && (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {factors.map((factor, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-1">{factor.title}</h3>
                <p className="text-sm text-gray-600">{factor.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800">Tip for Parents</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Visit schools during the registration period (usually February-March).
                  Many schools hold open days where you can tour and meet teachers.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PRIMARY_FAQS = [
  {
    q: "At what age should my child start school?",
    a: "Early childhood education can begin at age 3. Compulsory schooling starts at age 6 (Grade 1). Children should be 6 by September 1st of the school year."
  },
  {
    q: "What's the difference between Basic School and Infant School?",
    a: "Basic schools typically serve ages 3-6 and are often community-operated. Infant schools and infant departments are attached to primary schools and serve ages 4-6, providing more structured preparation for primary school."
  },
  {
    q: "How do I register my child for primary school?",
    a: "Registration usually happens in February-March for September entry. You'll need: birth certificate, immunization record, passport-size photos, proof of address, and transfer letter if coming from another school."
  },
  {
    q: "What if my child needs special education support?",
    a: "Start by talking to the school about your child's needs. Some schools have Special Education Resource Teachers. The Ministry of Education also has a Special Education Unit that can provide assessments and support."
  },
  {
    q: "Can I send my child to a school outside my zone?",
    a: "Yes, but zoned schools give preference to children in their catchment area. For out-of-zone schools, you may need to apply earlier and there may be waiting lists."
  },
  {
    q: "What subjects are taught in primary school?",
    a: "Core subjects include: Language Arts (Reading, Writing), Mathematics, Science, Social Studies, Religious Education, and Physical Education. Many schools also offer Music, Art, and Spanish."
  },
  {
    q: "How is my child assessed throughout primary school?",
    a: "Children are assessed continuously through classwork and termly exams. Grade 4 Diagnostic Tests help identify students needing extra support. Grade 6 students sit the PEP exam."
  }
];

// ==================== IMPORTANT CONTACTS ====================
const ImportantContacts = () => {
  const contacts = [
    {
      name: "Ministry of Education",
      desc: "Official information, policies, school registration",
      phone: "876-922-1400",
      website: "https://moey.gov.jm"
    },
    {
      name: "Early Childhood Commission",
      desc: "Registration and standards for early childhood institutions",
      phone: "876-946-0291",
      website: "https://ecc.gov.jm"
    },
    {
      name: "Jamaica Teaching Council",
      desc: "Teacher licensing and standards",
      phone: "876-967-2449",
      website: "https://jtc.gov.jm"
    },
    {
      name: "Special Education Unit (MOE)",
      desc: "Support for children with special needs",
      phone: "876-922-1400",
      website: null
    }
  ];

  return (
    <div className="bg-gray-50 rounded-2xl p-6 mb-8">
      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
        <Info className="w-5 h-5 text-brand-600" />
        Important Contacts
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {contacts.map((contact, idx) => (
          <div key={idx} className="bg-white rounded-xl p-4">
            <h4 className="font-semibold text-gray-800">{contact.name}</h4>
            <p className="text-sm text-gray-500 mb-2">{contact.desc}</p>
            <div className="flex flex-wrap gap-3 text-sm">
              <a href={`tel:${contact.phone}`} className="text-brand-600 hover:underline">
                {contact.phone}
              </a>
              {contact.website && (
                <a
                  href={contact.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 hover:underline flex items-center gap-1"
                >
                  Website
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== MAIN PRIMARY EDUCATION HUB ====================
export const PrimaryEducationHub = ({ setPage, goBack }) => {
  const educationStages = [
    {
      icon: Baby,
      title: "Early Childhood",
      meta: "Ages 3-5",
      description: "The foundation years. Children develop social skills, basic literacy, and school readiness.",
      features: [
        "Basic schools and infant schools prepare children for primary",
        "Focus on play-based learning and social development",
        "Early Childhood Commission regulates standards",
        "Many communities have affordable basic school options",
        "PATH benefits available for eligible families"
      ]
    },
    {
      icon: School,
      title: "Primary School",
      meta: "Ages 6-11",
      description: "Grades 1-6. Building fundamental skills in reading, writing, mathematics, and critical thinking.",
      features: [
        "Compulsory education begins at age 6",
        "Core subjects: Language Arts, Math, Science, Social Studies",
        "Grade 4 Diagnostic Test identifies students needing support",
        "Grade 6: Primary Exit Profile (PEP) for high school placement",
        "Many schools offer feeding programmes (breakfast/lunch)"
      ]
    },
    {
      icon: BookOpen,
      title: "All-Age Schools",
      meta: "Ages 6-15",
      description: "Some schools serve both primary and lower secondary (Grades 1-9), often in rural areas.",
      features: [
        "Combine primary and lower secondary education",
        "Common in rural parishes with smaller populations",
        "Students can complete up to Grade 9",
        "May offer vocational skills training",
        "Some upgraded to Junior High or full secondary"
      ]
    }
  ];

  const relatedResources = [
    { title: 'How School Works', desc: 'Full education system guide', icon: BookOpen, page: 'how-school-works' },
    { title: 'Financial Assistance', desc: 'PATH, bursaries, grants', icon: Award, page: 'funding' },
    { title: 'Child Protection', desc: 'Safety resources', icon: Shield, page: 'child-protection' }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-door-primary to-door-primary-dark rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
          <School className="w-5 h-5" />
          <span>Primary Education Guide</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black mb-3">
          Prep & Primary Schools in Jamaica
        </h1>
        <p className="text-lg text-white/90 mb-6 max-w-2xl">
          Everything parents need to know about early childhood and primary education.
          From basic school to PEP - we've got you covered.
        </p>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setPage('exams-pep')}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
          >
            <FileText className="w-4 h-4" /> PEP Guide
          </button>
          <button
            onClick={() => setPage('enrollment')}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
          >
            <Users className="w-4 h-4" /> Enrollment Info
          </button>
          <button
            onClick={() => setPage('special-education')}
            className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2"
          >
            <Heart className="w-4 h-4" /> Special Education
          </button>
        </div>
      </div>

      {/* Listings all live on /all-schools now; this hands off with Primary
          preselected. */}
      <DirectoryLink
        setPage={setPage}
        level="Primary"
        label="Search prep & primary schools"
        count={LEVEL_COUNTS.Primary}
        className="mb-8"
      />

      {/* Education Stages */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Understanding the Journey</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {educationStages.map((stage, idx) => (
            <ExpandableCard key={idx} accent="primary" {...stage}>
              <FeatureList items={stage.features} />
            </ExpandableCard>
          ))}
        </div>
      </div>

      {/* PEP Information */}
      <PEPInfoSection setPage={setPage} />

      {/* Choosing a School */}
      <ChoosingSchoolSection />

      {/* FAQs */}
      <FAQAccordion faqs={PRIMARY_FAQS} />

      {/* Important Contacts */}
      <ImportantContacts />

      {/* Related Resources */}
      <ResourceLinkGrid title="Related Resources" items={relatedResources} setPage={setPage} />
    </div>
  );
};

export default PrimaryEducationHub;
